/**
 * SEO Manager for Dynamic Meta Tag Manipulation
 * Ensures SPAs are SEO-friendly by updating head tags on route changes.
 */
export const SEOManager = {
    /**
     * Update Meta Tags dynamically
     * @param {Object} meta
     * @param {string} meta.title - Page Title
     * @param {string} meta.description - Page Description
     * @param {string} meta.image - OG Image URL
     * @param {string} meta.keywords - Comma separated keywords
     */
    updateMeta({ title, description, image, keywords }) {
        // 1. Update Title
        if (title) {
            document.title = `${title} | SSULWHA`;
            this.setMeta('og:title', title);
            this.setMeta('twitter:title', title);
        }

        // 2. Update Description
        if (description) {
            this.setMeta('description', description);
            this.setMeta('og:description', description);
            this.setMeta('twitter:description', description);
        }

        // 3. Update Image
        if (image) {
            this.setMeta('og:image', image);
            this.setMeta('twitter:image', image);
        }

        // 4. Update Keywords
        if (keywords) {
            this.setMeta('keywords', keywords);
        }
    },

    /**
     * Helper to set meta tag content
     * Handles both 'name' and 'property' attributes
     */
    setMeta(name, content) {
        let element = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);

        if (!element) {
            // Create if missing
            element = document.createElement('meta');
            // Check if it's OG (property) or Standard (name)
            if (name.startsWith('og:')) {
                element.setAttribute('property', name);
            } else {
                element.setAttribute('name', name);
            }
            document.head.appendChild(element);
        }

        element.setAttribute('content', content);
    },

    /**
     * Reset to Defaults (e.g. on Home route)
     */
    reset() {
        this.updateMeta({
            title: "홈",
            description: "AI 기술과 예술이 결합된 차세대 웹툰/웹소설 플랫폼, 썰화(SSULWHA).",
            image: "/og.webp",
            keywords: "웹툰, 웹소설, AI 웹툰, AI 소설, 창작 플랫폼, 썰화"
        });
    },

    /**
     * Inject JSON-LD Schema (Phase 13)
     */
    setJsonLd(data) {
        let script = document.getElementById('json-ld-schema');
        if (!script) {
            script = document.createElement('script');
            script.id = 'json-ld-schema';
            script.type = 'application/ld+json';
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
    },

    /**
     * SEO Helper: Series Detail Page
     * Injects BreadcrumbList and CreativeWork schema for GEO.
     */
    updateSeriesSEO(seriesData, authorName = 'Unknown', aiMetadata = null) {
        const title = seriesData.title;
        let desc = seriesData.description || `Read ${title} on SSULWHA.`;
        const image = seriesData.thumbnailUrl || '/og.webp';

        // [GEO] Inject AI Vibes
        if (aiMetadata && aiMetadata.vibe_summary) {
            const vibes = aiMetadata.vibe_summary.split(',').slice(0, 3).join(', ');
            desc = `[${vibes}] ${desc}`;
        }

        const baseKeywords = [`${title}`, `${authorName}`, "웹툰", "웹소설", "SSULWHA"];
        const customKeywords = Array.isArray(seriesData.seoKeywords) ? seriesData.seoKeywords : [];
        const aiKeywords = (aiMetadata && aiMetadata.vibe_summary) ? aiMetadata.vibe_summary.split(',').slice(0, 5) : [];

        const finalKeywords = [...baseKeywords, ...customKeywords, ...aiKeywords].join(', ');

        this.updateMeta({
            title: title + ` - ${authorName}`,
            description: desc,
            image: image,
            keywords: finalKeywords
        });

        // [GEO] Dynamic Schema Selection
        // Cine Novel -> VisualArtwork (Immersive)
        // Webtoon/Novel -> ComicSeries (Standard)
        const isCineNovel = seriesData.type === 'davinci';
        const schemaType = isCineNovel ? "VisualArtwork" : "ComicSeries";

        const creativeWorkSchema = {
            "@type": schemaType,
            "headline": title,
            "image": image,
            "author": {
                "@type": "Person",
                "name": authorName
            },
            "genre": seriesData.genre || "Fiction",
            "description": desc,
            "provider": {
                "@type": "Organization",
                "name": "SSULWHA",
                "url": "https://ssulwha.world"
            }
        };

        if (isCineNovel) {
            creativeWorkSchema.artMedium = "Immersive Webtoon";
            creativeWorkSchema.artform = "Digital";
        }

        const schema = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://ssulwha.world/"
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": seriesData.type === 'webnovel' ? 'Webnovel' : 'Webtoon',
                            "item": `https://ssulwha.world/${seriesData.type === 'webnovel' ? 'novel' : 'webtoon'}`
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": title
                        }
                    ]
                },
                creativeWorkSchema
            ]
        };
        this.setJsonLd(schema);
    },

    /**
     * SEO Helper: Viewer Page (Episode)
     */
    updateEpisodeSEO(seriesTitle, episodeTitle, authorName) {
        const fullTitle = `${episodeTitle} - ${seriesTitle}`;
        this.updateMeta({
            title: fullTitle,
            description: `Read ${episodeTitle} of ${seriesTitle}.`,
            keywords: `${seriesTitle}, ${episodeTitle}, ${authorName}, webtoon`
        });

        // Simple Article Schema for Episode
        const schema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": fullTitle,
            "author": {
                "@type": "Person",
                "name": authorName
            },
            "publisher": {
                "@type": "Organization",
                "name": "SSULWHA",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://ssulwha.world/icon-512.png"
                }
            }
        };
        this.setJsonLd(schema);
    }
};
